import { pyodideManager } from "@/components/pyodide/PyodideManager";

import { buildHintExecutions, executionToPhantomHint } from "./getPhantomHints";

import type { HintExecution, TableCellFormula } from "./types";
import type { PhantomHint } from "@/components/suggestions/types";

/**
 * Executions for a table cell's columnar formula, independent of the cursor. Shared by the
 * phantom-hint preview and the computed-node evaluator so a computed cell recomputes from the same
 * formula its hint previewed.
 */
export function getTableCellExecutions(formula: TableCellFormula): HintExecution[] {
  return buildHintExecutions(formula.expression, 0, formula.scope, false);
}

/**
 * The single hint for one table cell, computed from an explicit columnar formula (rather than the
 * cursor position). A cell offers no command choice: its `getHint` tries the candidate commands in
 * order and resolves to the first that yields a real (non-equal) result.
 * Used to fill every empty data cell of a formula row at once.
 */
export function getTableCellHint(formula: TableCellFormula): PhantomHint | null {
  if (!pyodideManager.isReady) return null;
  const candidates = getTableCellExecutions(formula).map(executionToPhantomHint);
  if (candidates.length === 0) return null;

  return {
    ...candidates[0],
    getHint: async () => {
      for (const candidate of candidates) {
        // eslint-disable-next-line no-await-in-loop -- first candidate with a real result wins
        const res = await candidate.getHint();
        if (res && !res.equals && res.json) return res;
      }
      return null;
    },
  };
}
